import type { SupportAccessRequest, User } from '../types';
import { supportRequests, users } from './mockData';
import { respond } from './respond';
import { addDoc, collection, getDocs } from 'firebase/firestore';
import { scope } from './scope';
import { db } from '../lib/firebase';
import { isReal } from './store';
import { callServer } from './server';
import { addAudit } from './auditService';

export async function getSupportRequests(facilityId: string): Promise<SupportAccessRequest[]> {
  if (isReal()) {
    const snap = await getDocs(collection(db!, 'facilities', facilityId, 'supportRequests'));
    const now = new Date().toISOString();
    return snap.docs
      .map((d) => {
        const r = { ...(d.data() as SupportAccessRequest & { requestedByName?: string }), id: d.id };
        // Stored as the super admin's user id (checked by the rules); show the name.
        const shown = { ...r, requestedBy: r.requestedByName ?? r.requestedBy };
        return shown.status === 'approved' && shown.expiresAt && shown.expiresAt < now ? { ...shown, status: 'expired' as const } : shown;
      })
      .sort((a, b) => b.requestedAt.localeCompare(a.requestedAt));
  }
  return respond(supportRequests.filter((r) => r.facilityId === facilityId).sort((a, b) => b.requestedAt.localeCompare(a.requestedAt)));
}

export async function requestSupportAccess(facilityId: string, reason: string, requestedBy: string): Promise<SupportAccessRequest> {
  if (isReal()) {
    const r = { facilityId, requestedBy: scope().uid ?? '', requestedByName: requestedBy, reason, requestedAt: new Date().toISOString(), status: 'pending' as const };
    const ref = await addDoc(collection(db!, 'facilities', facilityId, 'supportRequests'), r);
    return { ...r, requestedBy, id: ref.id };
  }
  const r: SupportAccessRequest = { id: `sar-${Date.now()}`, facilityId, requestedBy, reason, requestedAt: new Date().toISOString(), status: 'pending' };
  supportRequests.push(r);
  return respond(r);
}

export async function inviteUser(input: Omit<User, 'id' | 'active'>): Promise<User> {
  // The server creates the login with its role and clinic, and emails our own one-time 24-hour link (functions/src/invites.ts).
  if (isReal()) return callServer<User>('inviteUser', input);
  const u: User = { ...input, id: `u-${Date.now()}`, active: true, inviteStatus: 'sent', inviteEmailSent: true, inviteExpiresAt: new Date(Date.now() + 864e5).toISOString() };
  users.push(u);
  return respond(u, 300);
}

/** A new 24-hour link for someone who hasn't set a password yet (the old link stops working). */
export async function resendInvite(uid: string): Promise<{ emailSent: boolean; inviteExpiresAt: string }> {
  if (isReal()) return callServer('resendInvite', { uid });
  const u = users.find((x) => x.id === uid);
  const r = { emailSent: true, inviteExpiresAt: new Date(Date.now() + 864e5).toISOString() };
  if (u) Object.assign(u, { inviteStatus: 'sent', inviteEmailSent: true, inviteExpiresAt: r.inviteExpiresAt });
  return respond(r, 300);
}

export interface InviteCheck { state: 'ok' | 'invalid' | 'used' | 'expired'; name?: string; email?: string; clinicName?: string }

/** Welcome page: is the link in the email still good? Works before sign-in. */
export async function checkInvite(token: string): Promise<InviteCheck> {
  if (!token) return { state: 'invalid' };
  if (isReal()) return callServer<InviteCheck>('checkInvite', { token });
  return respond({ state: 'ok', name: 'Demo Doctor', email: 'demo.doctor@example.com', clinicName: 'Sharma Hospital' } as InviteCheck, 300);
}

/** Welcome page: set the password. The link is used up. */
export async function acceptInvite(token: string, password: string): Promise<{ email: string }> {
  if (isReal()) return callServer<{ email: string }>('acceptInvite', { token, password });
  return respond({ email: 'demo.doctor@example.com' }, 300);
}

// Demo only: the person the last reset link was "sent" to, so the demo reset page shows the same person.
let demoResetUid = '';

/** C7: send someone a one-time, 1-hour link to set a new password. The link is emailed, never shown here. */
export async function sendResetLink(uid: string): Promise<{ emailSent: boolean }> {
  if (isReal()) return callServer('sendResetLink', { uid, identityChecked: true });
  const s = scope();
  demoResetUid = uid;
  addAudit(s.actor, 'user.reset_link_sent', uid, s.facilityId, 'identity checked by phone');
  return respond({ emailSent: true }, 300);
}

/** Reset page: is the link still good? Works before sign-in. */
export async function checkReset(token: string): Promise<InviteCheck> {
  if (!token) return { state: 'invalid' };
  if (isReal()) return callServer<InviteCheck>('checkReset', { token });
  const u = users.find((x) => x.id === demoResetUid) ?? users.find((x) => x.role === 'doctor');
  return respond({ state: 'ok', name: u?.name ?? '', email: u?.email ?? '' } as InviteCheck, 300);
}

/** Reset page: set the new password. The link is used up and the person is signed out everywhere else. */
export async function acceptReset(token: string, password: string): Promise<{ email: string }> {
  if (isReal()) return callServer<{ email: string }>('acceptReset', { token, password });
  const u = users.find((x) => x.id === demoResetUid) ?? users.find((x) => x.role === 'doctor');
  return respond({ email: u?.email ?? '' }, 300);
}

/** C10: sign this person out on every phone and computer (their other sign-ins stop within the hour at most). */
export async function logOutEverywhere(): Promise<void> {
  if (isReal()) {
    await callServer('logOutEverywhere', {});
    return;
  }
  addAudit(scope().actor, 'user.logged_out_everywhere', scope().uid ?? '', scope().facilityId);
  await respond(undefined);
}
